import dayjs from "dayjs";
import React, { useState, useMemo } from "react";
import {
  Collapse,
  Table,
  Badge,
  Button,
  Card,
  Form,
  InputGroup,
  OverlayTrigger,
  Tooltip,
  Row,
  Col,
} from "react-bootstrap";
import {
  FaChevronDown,
  FaChevronRight,
  FaExclamationTriangle,
  FaChartBar,
  FaUser,
  FaMobileAlt,
  FaUsers,
  FaEdit,
  FaClock,
  FaLink,
  FaArrowUp,
  FaArrowDown,
  FaArrowRight,
  FaStar,
  FaTimes,
  FaSearch,
  FaInfoCircle,
  FaFilter,
} from "react-icons/fa";
import "./ChangesGrid.css";

const dateFormat = "MM/DD/YYYY hh:mm A z";

// Generated by GitHub Copilot
// Helper to format int64 time values as date strings
function formatValueForDisplay(value) {
  // If value is null/undefined/empty, return as is
  if (value === null || value === undefined || value === "") return value;
  // If value is a number or numeric string
  const num = typeof value === "number" ? value : Number(value);
  if (!isNaN(num) && isFinite(num)) {
    // Heuristic: treat as ms if > 10^11, as s if > 10^9
    if (num > 1e11) {
      // Milliseconds since epoch
      return dayjs(num).format(dateFormat);
    } else if (num > 1e9) {
      // Seconds since epoch
      return dayjs(num * 1000).format(dateFormat);
    }
  }
  return value;
}

// Enhanced field descriptions for tooltips
const fieldDescriptions = {
  priority: "Incident priority level - P1 (Critical) to P4 (Low)",
  status: "Current incident status - Open, In Progress, Resolved, Closed",
  majorIncident: "Designation as a major incident affecting multiple systems",
  incidentCommander: "Person responsible for coordinating incident response",
  majorIncidentManager: "Senior leader overseeing major incident response",
  affectedApplications: "Systems and applications impacted by the incident",
  affectedUsers: "Number or description of users affected",
  description: "Detailed description of the incident",
  notes: "Additional notes and updates about the incident",
  incidentStartTime: "When the incident was first detected or reported",
  incidentEndTime: "When the incident was resolved or closed",
  contacts: "Key contacts involved in incident response",
  externalLinks: "Links to external resources, documentation, or tools",
  slackChannel: "Slack channel for incident communication",
  auditTrail: "System-generated audit information",
  s3Version: "Version identifier for data storage",
  lastModified: "Timestamp of last modification",
};

// Business impact categorization for advanced grouping
const businessImpactLevels = {
  critical: {
    priority: 1,
    description: "Changes affecting system availability or security",
    color: "danger",
  },
  high: {
    priority: 2,
    description: "Changes affecting key personnel or major functionality",
    color: "warning",
  },
  medium: {
    priority: 3,
    description: "Changes affecting incident details or timeline",
    color: "info",
  },
  low: {
    priority: 4,
    description: "Changes to documentation or minor details",
    color: "success",
  },
  system: {
    priority: 5,
    description: "System-generated changes for auditing",
    color: "secondary",
  },
};

// Enhanced field type categories with business impact mapping
const fieldCategories = {
  critical: {
    color: "danger",
    icon: FaExclamationTriangle,
    fields: ["priority", "status", "majorIncident"],
    label: "Critical",
    businessImpact: "critical",
    description: "Changes that directly affect incident severity or resolution",
  },
  important: {
    color: "warning",
    icon: FaUser,
    fields: [
      "incidentCommander",
      "majorIncidentManager",
      "affectedApplications",
    ],
    label: "Important",
    businessImpact: "high",
    description: "Changes affecting key personnel or system scope",
  },
  moderate: {
    color: "info",
    icon: FaEdit,
    fields: [
      "incidentStartTime",
      "incidentEndTime",
      "affectedUsers",
      "description",
    ],
    label: "Moderate",
    businessImpact: "medium",
    description: "Changes to incident timeline or impact details",
  },
  minor: {
    color: "success",
    icon: FaEdit,
    fields: ["notes", "contacts", "externalLinks"],
    label: "Minor",
    businessImpact: "low",
    description: "Changes to documentation or contact information",
  },
  system: {
    color: "secondary",
    icon: FaChartBar,
    fields: ["auditTrail", "s3Version", "lastModified"],
    label: "System",
    businessImpact: "system",
    description: "System-generated audit and versioning information",
  },
};

// Field-specific icon mapping
const fieldIcons = {
  priority: FaExclamationTriangle,
  status: FaChartBar,
  incidentCommander: FaUser,
  majorIncidentManager: FaUser,
  affectedApplications: FaMobileAlt,
  affectedUsers: FaUsers,
  description: FaEdit,
  notes: FaEdit,
  incidentStartTime: FaClock,
  incidentEndTime: FaClock,
  contacts: FaUser,
  externalLinks: FaLink,
  slackChannel: FaLink,
};

// Helper function to determine field category
const getFieldCategory = (fieldName) => {
  if (!fieldName || typeof fieldName !== "string") {
    return fieldCategories.minor; // Default to minor for invalid field names
  }

  for (const [category, config] of Object.entries(fieldCategories)) {
    if (
      config.fields.some((field) =>
        fieldName.toLowerCase().includes(field.toLowerCase())
      )
    ) {
      return { category, ...config };
    }
  }
  return fieldCategories.minor; // Default to minor
};

// Helper function to get appropriate icon for field
const getFieldIcon = (fieldName) => {
  if (!fieldName || typeof fieldName !== "string") {
    return FaEdit; // Default icon for invalid field names
  }

  const iconKey = Object.keys(fieldIcons).find((key) =>
    fieldName.toLowerCase().includes(key.toLowerCase())
  );
  return iconKey ? fieldIcons[iconKey] : FaEdit;
};

// Helper function to determine value change direction
const getChangeDirection = (fieldName, oldValue, newValue) => {
  if (!fieldName || typeof fieldName !== "string") {
    return { icon: FaArrowRight, type: "modified", color: "info" }; // Default for invalid field names
  }

  const field = fieldName.toLowerCase();

  // Priority changes (P1 is higher priority than P4)
  if (field.includes("priority")) {
    const oldNum = parseInt(oldValue?.replace("P", "") || "4");
    const newNum = parseInt(newValue?.replace("P", "") || "4");
    if (oldNum > newNum)
      return { icon: FaArrowUp, type: "escalation", color: "danger" };
    if (oldNum < newNum)
      return { icon: FaArrowDown, type: "de-escalation", color: "success" };
    return { icon: FaArrowRight, type: "transition", color: "info" };
  }

  // Status transitions
  if (field.includes("status")) {
    return { icon: FaArrowRight, type: "transition", color: "info" };
  }

  // New values
  if (!oldValue && newValue) {
    return { icon: FaStar, type: "new", color: "primary" };
  }

  // Removed values
  if (oldValue && !newValue) {
    return { icon: FaTimes, type: "removed", color: "danger" };
  }

  // Default modification
  return { icon: FaArrowRight, type: "modified", color: "info" };
};

// Advanced helper function to get field description for tooltips
const getFieldDescription = (fieldName) => {
  if (!fieldName || typeof fieldName !== "string") {
    return "Field information unavailable";
  }

  const descKey = Object.keys(fieldDescriptions).find((key) =>
    fieldName.toLowerCase().includes(key.toLowerCase())
  );
  return descKey
    ? fieldDescriptions[descKey]
    : `Information about ${fieldName}`;
};

// Enhanced helper function to get business impact level
const getBusinessImpact = (fieldName) => {
  const category = getFieldCategory(fieldName);
  return (
    businessImpactLevels[category.businessImpact] || businessImpactLevels.low
  );
};

// Helper function to create informational tooltips
const createTooltip = (content, placement = "top") => (
  <Tooltip id="changes-tooltip" placement={placement}>
    {content}
  </Tooltip>
);

// Performance optimization: memoized change filtering and sorting

// Exclude system-generated fields from change tracking
const EXCLUDED_FIELDS = [
  "interestedparties",
  "notes",
  "temporary",
  "temporarynumber",
  "currentversion",
  "previousversion",
];

const useOptimizedChanges = (changes, searchTerm, categoryFilter) => {
  return useMemo(() => {
    if (!changes || changes.length === 0) {
      return [];
    }

    let filteredChanges = [...changes].filter((change) => {
      if (!change || typeof change !== "object") return false;
      const fieldName = (change.field || change.fieldName || "").toLowerCase();
      // Exclude system-generated fields
      return !EXCLUDED_FIELDS.some((excluded) => fieldName.includes(excluded));
    });

    // Apply search filter
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filteredChanges = filteredChanges.filter((change) => {
        const fieldName = (
          change.field ||
          change.fieldName ||
          ""
        ).toLowerCase();
        const oldValue = (change.oldValue || "").toString().toLowerCase();
        const newValue = (change.newValue || "").toString().toLowerCase();
        return (
          fieldName.includes(term) ||
          oldValue.includes(term) ||
          newValue.includes(term)
        );
      });
    }

    // Apply category filter
    if (categoryFilter && categoryFilter !== "all") {
      filteredChanges = filteredChanges.filter((change) => {
        const fieldName = change.field || change.fieldName;
        const category = getFieldCategory(fieldName);
        return category.category === categoryFilter;
      });
    }

    // Sort by business impact and then by field name
    return filteredChanges.sort((a, b) => {
      const aFieldName = a.field || a.fieldName;
      const bFieldName = b.field || b.fieldName;
      const aImpact = getBusinessImpact(aFieldName);
      const bImpact = getBusinessImpact(bFieldName);

      // First sort by business impact priority
      if (aImpact.priority !== bImpact.priority) {
        return aImpact.priority - bImpact.priority;
      }

      // Then sort alphabetically by field name
      return (aFieldName || "").localeCompare(bFieldName || "");
    });
  }, [changes, searchTerm, categoryFilter]);
};

const ChangesGrid = ({ changes, version, enableAdvancedFeatures = true }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // Use optimized changes processing
  const optimizedChanges = useOptimizedChanges(
    changes,
    searchTerm,
    categoryFilter
  );

  // Calculate category statistics for enhanced overview
  const categoryStats = useMemo(() => {
    if (!optimizedChanges || optimizedChanges.length === 0) {
      return {};
    }
    return optimizedChanges.reduce((acc, change) => {
      if (change && typeof change === "object") {
        const fieldName = change.field || change.fieldName;
        const category = getFieldCategory(fieldName);
        acc[category.category] = (acc[category.category] || 0) + 1;
      }
      return acc;
    }, {});
  }, [optimizedChanges]);

  // Early return after all hooks
  if (!changes || changes.length === 0) {
    return null;
  }

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle category filter change
  const handleCategoryFilterChange = (e) => {
    setCategoryFilter(e.target.value);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setCategoryFilter("all");
  };

  return (
    <div className="changes-section mt-3">
      <div className="changes-header">
        <Button
          variant="link"
          onClick={toggleExpanded}
          className="p-0 text-decoration-none changes-toggle"
          aria-expanded={isExpanded}
        >
          {isExpanded ? (
            <FaChevronDown className="me-2" />
          ) : (
            <FaChevronRight className="me-2" />
          )}
          <span className="fw-bold">
            {changes.length} Field{changes.length !== 1 ? "s" : ""} Changed
            {optimizedChanges.length !== changes.length &&
              ` (${optimizedChanges.length} visible)`}
          </span>
        </Button>

        {/* Enhanced summary badges with tooltips */}

        <div className="changes-summary ms-3 d-flex align-items-center">
          {Object.entries(categoryStats).map(([category, count]) => {
            const config = fieldCategories[category];
            if (!config) return null;
            const Icon = config.icon;
            return (
              <OverlayTrigger
                key={category}
                placement="top"
                overlay={createTooltip(
                  `${config.label}: ${count} change${
                    count !== 1 ? "s" : ""
                  } - ${config.description}`
                )}
              >
                <div
                  className={`d-flex align-items-center justify-content-center me-2 text-${config.color}`}
                  style={{ cursor: "help", minWidth: 28 }}
                >
                  <Icon size={16} className="me-1" />
                  <span className="small fw-bold">{count}</span>
                </div>
              </OverlayTrigger>
            );
          })}

          {enableAdvancedFeatures && (
            <OverlayTrigger
              placement="top"
              overlay={createTooltip(
                "Show advanced filtering and search options"
              )}
            >
              <Button
                variant="outline-secondary"
                size="sm"
                className="ms-2"
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              >
                <FaFilter size={12} className="me-1" />
                Filters
              </Button>
            </OverlayTrigger>
          )}
        </div>
      </div>

      {/* Advanced search and filtering options */}
      {enableAdvancedFeatures && showAdvancedOptions && (
        <Card className="mt-2 border-light">
          <Card.Body className="py-2">
            <Row>
              <Col md={6}>
                <InputGroup size="sm">
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search field names or values..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </InputGroup>
              </Col>
              <Col md={4}>
                <Form.Select
                  size="sm"
                  value={categoryFilter}
                  onChange={handleCategoryFilterChange}
                >
                  <option value="all">All Categories</option>
                  {Object.entries(fieldCategories).map(([key, config]) => (
                    <option key={key} value={key}>
                      {config.label} Changes
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={clearFilters}
                  disabled={!searchTerm && categoryFilter === "all"}
                >
                  Clear
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <Collapse in={isExpanded}>
        <div className="changes-content">
          {optimizedChanges.length === 0 ? (
            <Card className="mt-2">
              <Card.Body className="text-center text-muted py-4">
                <FaInfoCircle size={24} className="mb-2" />
                <div>No changes match your current filters</div>
                <Button variant="link" size="sm" onClick={clearFilters}>
                  Clear filters to show all changes
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <Card className="mt-2">
              <Card.Body className="p-0">
                <Table responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th width="25%">
                        Field
                        <OverlayTrigger
                          placement="top"
                          overlay={createTooltip(
                            "Field names are categorized by business impact"
                          )}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            <FaInfoCircle
                              size={12}
                              className="ms-1 text-muted"
                              style={{ cursor: "help" }}
                            />
                          </span>
                        </OverlayTrigger>
                      </th>
                      <th width="35%">Previous Value</th>
                      <th width="35%">New Value</th>
                      <th width="5%" className="text-center">
                        Change
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      optimizedChanges
                        .map((change, index) => {
                          // Safety check for change object
                          if (!change || typeof change !== "object") {
                            return null;
                          }

                          const fieldName =
                            change.field ||
                            change.fieldName ||
                            `Unknown Field ${index + 1}`;
                          const oldValue = change.oldValue;
                          const newValue = change.newValue;

                          const category = getFieldCategory(fieldName);
                          const FieldIcon = getFieldIcon(fieldName);
                          const changeDirection = getChangeDirection(
                            fieldName,
                            oldValue,
                            newValue
                          );
                          const DirectionIcon = changeDirection.icon;
                          const fieldDescription =
                            getFieldDescription(fieldName);
                          const businessImpact = getBusinessImpact(fieldName);

                          return (
                            <tr key={index} className="change-row">
                              <td>
                                <div className="d-flex align-items-center">
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={createTooltip(
                                      `${category.label} - ${businessImpact.description}`
                                    )}
                                  >
                                    <Badge
                                      bg={category.color}
                                      className="me-2 change-category-badge"
                                      style={{ cursor: "help" }}
                                    >
                                      <FieldIcon size={12} />
                                    </Badge>
                                  </OverlayTrigger>

                                  <OverlayTrigger
                                    placement="right"
                                    overlay={createTooltip(fieldDescription)}
                                  >
                                    <span
                                      className="field-name"
                                      style={{ cursor: "help" }}
                                    >
                                      {fieldName}
                                    </span>
                                  </OverlayTrigger>
                                </div>
                              </td>
                              <td>
                                <span
                                  className={`old-value ${
                                    !oldValue ? "text-muted fst-italic" : ""
                                  }`}
                                >
                                  {oldValue
                                    ? formatValueForDisplay(oldValue)
                                    : "(empty)"}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`new-value ${
                                    !newValue
                                      ? "text-muted fst-italic"
                                      : "fw-bold"
                                  }`}
                                >
                                  {newValue
                                    ? formatValueForDisplay(newValue)
                                    : "(empty)"}
                                </span>
                              </td>
                              <td className="text-center">
                                <OverlayTrigger
                                  placement="left"
                                  overlay={createTooltip(
                                    `${
                                      changeDirection.type
                                        .charAt(0)
                                        .toUpperCase() +
                                      changeDirection.type.slice(1)
                                    } change`
                                  )}
                                >
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <DirectionIcon
                                      className={`text-${changeDirection.color}`}
                                      size={14}
                                      style={{ cursor: "help" }}
                                    />
                                  </span>
                                </OverlayTrigger>
                              </td>
                            </tr>
                          );
                        })
                        .filter(Boolean) /* Remove any null entries */
                    }
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </div>
      </Collapse>
    </div>
  );
};

export default ChangesGrid;
